import { Meeting_Step_Type_Enum, Thread_Type_Enum } from '@gql'

// Which thread type a meeting step accepts when adding (D4, 05/08/2026).
//
// Two steps hold threads and they differ only by what they let you add:
// `Threads` holds the tactical ones (the name is historical, see the enum's
// comment in database), `ThreadsGovernance` the governance ones.
//
// Returns undefined for any other step type, which means "no opinion": the
// caller then filters nothing.
export function threadTypeForStep(
  stepType: Meeting_Step_Type_Enum
): Thread_Type_Enum | undefined {
  switch (stepType) {
    case Meeting_Step_Type_Enum.Threads:
      return Thread_Type_Enum.Tactical
    case Meeting_Step_Type_Enum.ThreadsGovernance:
      return Thread_Type_Enum.Governance
    default:
      return undefined
  }
}

// Whether a step holds threads at all. Named on purpose: the same test decides
// which steps get a drag & drop context, and forgetting one of the two types
// there leaves a step silently empty, with no error — the shape of the D1 and
// REUN-01 bugs. One predicate, one place to update.
export function isThreadsStep(stepType: Meeting_Step_Type_Enum): boolean {
  return threadTypeForStep(stepType) !== undefined
}

// Whether a thread may be ADDED to a step from its picker.
//
// Deliberate fallback: a thread whose type is missing or unknown stays
// proposable everywhere. Unexpected data never deprives anyone of their tools
// — same rule as D3, where an unknown type shows every resolution rather than
// none.
export function isThreadAddableToStep(
  threadType: Thread_Type_Enum | null | undefined,
  stepType: Meeting_Step_Type_Enum
): boolean {
  const expected = threadTypeForStep(stepType)
  if (!expected) return true
  if (threadType !== Thread_Type_Enum.Tactical &&
      threadType !== Thread_Type_Enum.Governance) {
    return true
  }
  return threadType === expected
}

// Where a misplaced thread could go, or undefined when there is nothing to say.
//
// Returns undefined — meaning total silence — in every case but one: the thread
// has a known type, it contradicts the step holding it, AND a step of the right
// type exists in this meeting. We never show a closed door: signalling a
// mismatch with no way to fix it would only be a reproach.
//
// A thread with no type, or a type we don't know, is never signalled (D3: we
// don't guess). Nothing here ever moves a thread on its own — the gesture stays
// the human's, because `thread.type` can change mid-meeting and no data change
// should undo someone's filing.
export function findMoveTargetStep<
  S extends { type: Meeting_Step_Type_Enum },
>(
  threadType: Thread_Type_Enum | null | undefined,
  currentStepType: Meeting_Step_Type_Enum,
  steps: S[]
): S | undefined {
  const expected = threadTypeForStep(currentStepType)
  if (!expected) return undefined
  if (
    threadType !== Thread_Type_Enum.Tactical &&
    threadType !== Thread_Type_Enum.Governance
  ) {
    return undefined
  }
  if (threadType === expected) return undefined
  return steps.find((step) => threadTypeForStep(step.type) === threadType)
}

// Filters a picker's list. Kept separate from the predicate so callers can
// compose it with the REUN-02 scope filter rather than replace it.
export function filterThreadsForStep<T extends { type?: Thread_Type_Enum | null }>(
  threads: T[],
  stepType: Meeting_Step_Type_Enum
): T[] {
  const expected = threadTypeForStep(stepType)
  if (!expected) return threads
  return threads.filter((thread) => isThreadAddableToStep(thread.type, stepType))
}
